import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import LoaderComponent from '@/components/LoaderComponent';
import CommonImage from '@/components/CommonImage';
import ModalContainer from '@/components/modals/ModalContainer';

import { getPartTitle, renderSyncIcon } from '../utils';

const ConsumptionModal = ({
	fabricWiseMeasurements,
	onClose,
	cadConsumptions,
	isEditable = false,
	onReCostMeasurement
}) => {
	const [localMeasurements, setLocalMeasurements] = useState({});
	const [localCadConsumptions, setLocalCadConsumptions] = useState([]);
	const [editingRow, setEditingRow] = useState(null);
	const [editingCadRow, setEditingCadRow] = useState(null);
	const [changedPartKeys, setChangedPartKeys] = useState(new Set());
	const [isCadChanged, setIsCadChanged] = useState(false);
	const [cadErrors, setCadErrors] = useState({});
	const [measurementErrors, setMeasurementErrors] = useState({});
	const editingRowRef = useRef(null);
	const editingCadRowRef = useRef(null);
	const loader = useSelector((state) => state.loaderStore);

	const CAD_REQUIRED_FIELDS = ['waste_percentage'];
	const MEASUREMENT_REQUIRED_FIELDS = [
		'part',
		'length_cm',
		'allowance_length_cm',
		'width_cm',
		'allowance_width_cm',
		'number_of_fabric_pieces',
		'gsm',
		'wastage_percentage'
	];

	const validateCadConsumptions = () => {
		const errors = {};
		localCadConsumptions.forEach((cad, index) => {
			CAD_REQUIRED_FIELDS.forEach((field) => {
				const val = cad[field];
				if (val === '' || val === null || val === undefined) {
					if (!errors[index]) errors[index] = {};
					errors[index][field] = 'Required';
				}
			});
		});
		setCadErrors(errors);
		return Object.keys(errors).length === 0;
	};

	const validateMeasurements = () => {
		const errors = {};
		Object.entries(localMeasurements).forEach(([partKey, parts]) => {
			parts.forEach((part, partIndex) => {
				MEASUREMENT_REQUIRED_FIELDS.forEach((field) => {
					const val = part[field];
					if (val === '' || val === null || val === undefined) {
						if (!errors[partKey]) errors[partKey] = {};
						if (!errors[partKey][partIndex]) errors[partKey][partIndex] = {};
						errors[partKey][partIndex][field] = 'Required';
					}
				});
			});
		});
		setMeasurementErrors(errors);
		return Object.keys(errors).length === 0;
	};

	// sync localCadConsumptions from prop
	useEffect(() => {
		if (cadConsumptions && cadConsumptions.length > 0) {
			setLocalCadConsumptions(
				cadConsumptions?.map((cad) => ({
					...cad,
					waste_percentage: ((1 - parseFloat(cad?.cutting_waste_percentage)) * 100)?.toFixed(2)
				}))
			);
			setIsCadChanged(false);
			setCadErrors({});
		}
	}, [cadConsumptions]);

	// click-outside for measurement rows
	useEffect(() => {
		if (!editingRow) return;
		const handleClickOutside = (e) => {
			if (editingRowRef.current && !editingRowRef.current.contains(e.target)) {
				setEditingRow(null);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [editingRow]);

	// click-outside for CAD rows
	useEffect(() => {
		if (editingCadRow === null) return;
		const handleCadClickOutside = (e) => {
			if (editingCadRowRef.current && !editingCadRowRef.current.contains(e.target)) {
				setEditingCadRow(null);
			}
		};
		document.addEventListener('mousedown', handleCadClickOutside);
		return () => document.removeEventListener('mousedown', handleCadClickOutside);
	}, [editingCadRow]);

	useEffect(() => {
		if (Object.keys(fabricWiseMeasurements).length > 0) {
			let tmpObj = {};
			Object.entries(fabricWiseMeasurements).forEach(([partKey, measurements]) => {
				tmpObj[partKey] = measurements.map((measurement) => {
					return {
						...measurement,
						allowance_length_cm: (
							parseFloat(measurement?.final_length_with_allowance_cm) -
								parseFloat(measurement?.length_cm) || 0
						)?.toFixed(2),
						allowance_width_cm: (
							parseFloat(measurement?.final_width_with_allowance_cm) -
								parseFloat(measurement?.width_cm) || 0
						)?.toFixed(2)
					};
				});
			});
			setLocalMeasurements(tmpObj);
			setMeasurementErrors({});
		}
	}, [fabricWiseMeasurements]);

	const handleCellChange = (partKey, partIndex, field, value) => {
		setChangedPartKeys((prev) => new Set([...prev, partKey]));
		// clear error for the changed field
		setMeasurementErrors((prev) => {
			if (prev[partKey]?.[partIndex]?.[field]) {
				const updated = { ...prev };
				const updatedPart = { ...updated[partKey][partIndex] };
				delete updatedPart[field];
				updated[partKey] = { ...updated[partKey], [partIndex]: updatedPart };
				return updated;
			}
			return prev;
		});
		setLocalMeasurements((prev) => {
			const updated = { ...prev };
			const parts = [...updated[partKey]];
			const part = { ...parts[partIndex], [field]: value };
			part[field] = value;
			part.final_fabric_consumption_kg = calculateTotalConsumption(part)?.toFixed(2);
			parts[partIndex] = part;
			updated[partKey] = parts;
			return updated;
		});
	};

	const handleCadCellChange = (cadIndex, field, value) => {
		setIsCadChanged(true);
		// clear error for the changed field
		setCadErrors((prev) => {
			if (prev[cadIndex]?.[field]) {
				const updated = { ...prev };
				const updatedRow = { ...updated[cadIndex] };
				delete updatedRow[field];
				updated[cadIndex] = updatedRow;
				return updated;
			}
			return prev;
		});
		setLocalCadConsumptions((prev) => {
			const updated = [...prev];
			let cutting_waste_percentage = 1 - value / 100;
			let cad = updated[cadIndex];
			updated[cadIndex] = {
				...updated[cadIndex],
				[field]: value,
				cutting_waste_percentage: cutting_waste_percentage,
				consumption:
					(cad.fabric_length_inch * 1.1 * (cad.fabric_width_inch + 2) * cad.gsm) /
					1550 /
					1000 /
					cutting_waste_percentage /
					12
			};
			return updated;
		});
	};

	const isRowEditing = (partKey, partIndex) =>
		editingRow && editingRow.partKey === partKey && editingRow.partIndex === partIndex;

	const handleRowClick = (partKey, partIndex) => {
		if (!isEditable) return;
		setEditingRow({ partKey, partIndex });
	};

	if (!localMeasurements || Object.keys(localMeasurements).length === 0) {
		return (
			<LoaderComponent loading={false}>
				<ModalContainer className="width-1200-modal">
					<div className="common-popup conumption-modal w-100 body-scroll">
						<div className="common-popup-header border-bottom-0 d-flex justify-content-between">
							<div className="popup-tilte">
								<p className="semibold-14 gray_dark_01">Consumption Breakdown</p>
								<small className="regular-14 gray_dark_01">
									According to the measurement chart, fabric consumption has been calculated
									below:
								</small>
							</div>
							<div className="close-btn bg-white" onClick={onClose}>
								<CommonImage alt="close" src="/icons/close.svg" />
							</div>
						</div>
						<div className="common-popup-body scroll-y-label">
							<p className="text-center p-4">No consumption data available</p>
						</div>
					</div>
				</ModalContainer>
			</LoaderComponent>
		);
	}

	const renderCadConsumption = () => {
		if (localCadConsumptions && localCadConsumptions?.length > 0) {
			return (
				<div className={`consumption-table scroll-x-label mb-3`}>
					<h3 className="regular-14 mb-2 d-flex align-items-center gap-1">
						CAD Consumptions
						{isCadChanged && (
							<span
								title="Unsaved changes"
								className="sync-icon ml-1 cursor-pointer"
								style={{ display: 'inline-flex', alignItems: 'center' }}
								onClick={() => {
									if (!validateCadConsumptions()) return;
									onReCostMeasurement(
										'CAD_WASTAGE_PERCENTAGE_CHANGE',
										localCadConsumptions
									);
									setIsCadChanged(false);
								}}
							>
								{renderSyncIcon()}
							</span>
						)}
					</h3>
					<table className="table height-sm with-border mb-0">
						<thead>
							<tr>
								<th>Shell No</th>
								<th>Length (inch)</th>
								<th>L. Allow (inch)</th>
								<th>Width (inch)</th>
								<th>W. Allow (inch)</th>
								<th>GSM</th>
								<th>Wastage (%)</th>
								<th>Consumption (Kg)</th>
							</tr>
						</thead>
						<tbody>
							{localCadConsumptions?.map((cad, index) => {
								const isCadRowEditing = editingCadRow === index;
								return (
									<tr
										key={`consumption_row_${index}`}
										ref={isCadRowEditing ? editingCadRowRef : null}
										onClick={() => isEditable && setEditingCadRow(index)}
										style={{ cursor: isEditable ? 'pointer' : 'default' }}
										className={isCadRowEditing ? 'row-editing' : ''}
									>
										<td className="body-part-column">
											<p>{getPartTitle(`part${index + 1}`)}</p>
										</td>
										<td className="length-column">
											<p>{cad?.fabric_length_inch}</p>
										</td>
										<td className="length-with-allowance-column">
											<p>10%</p>
										</td>
										<td className="width-column">
											<p>{cad?.fabric_width_inch}</p>
										</td>
										<td className="width-with-allowance-column">
											<p>2</p>
										</td>
										<td className="gsm-column">
											<p>{cad?.gsm}</p>
										</td>
										<td className="wastage-column">
											{isEditable && isCadRowEditing ? (
												<>
													<input
														className={`table-editable-input${cadErrors[index]?.waste_percentage ? ' input-error' : ''}`}
														type="number"
														autoFocus
														value={cad.waste_percentage ?? ''}
														onChange={(e) =>
															handleCadCellChange(
																index,
																'waste_percentage',
																e.target.value
															)
														}
													/>
													{cadErrors[index]?.waste_percentage && (
														<small className="red">
															{cadErrors[index].waste_percentage}
														</small>
													)}
												</>
											) : (
												<>
													<p
														className={
															cadErrors[index]?.waste_percentage
																? 'text-danger'
																: ''
														}
													>
														{(
															(1 - parseFloat(cad?.cutting_waste_percentage)) *
															100
														)?.toFixed(2)}
													</p>
													{cadErrors[index]?.waste_percentage && (
														<small className="red">
															{cadErrors[index].waste_percentage}
														</small>
													)}
												</>
											)}
										</td>
										<td className={'consumption-column'}>
											<p>{cad?.consumption?.toFixed(2)}</p>
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
					<div className="total-consumption table-footer p-2">
						<div className="d-flex justify-content-start">
							<p className="regular-14 gray_dark_01 weight-500">Total Consumption</p>
							<p className="regular-14 gray_dark_01 ml-2 weight-500">
								{localCadConsumptions
									?.reduce((total, cad) => total + parseFloat(cad?.consumption || 0), 0)
									?.toFixed(2)}{' '}
								Kg
							</p>
						</div>
					</div>
				</div>
			);
		}
	};

	const renderCell = (partKey, part, partIndex, field, displayValue) => {
		const hasError = measurementErrors[partKey]?.[partIndex]?.[field];
		if (!isEditable || !isRowEditing(partKey, partIndex)) {
			return (
				<>
					<p className={hasError ? 'text-danger' : ''}>{displayValue}</p>
					{hasError && <small className="red">{hasError}</small>}
				</>
			);
		}
		return (
			<>
				<input
					className={`table-editable-input${hasError ? ' input-error' : ''}`}
					type="number"
					value={displayValue ?? ''}
					autoFocus={field === 'length_cm'}
					onChange={(e) => handleCellChange(partKey, partIndex, field, e.target.value)}
				/>
				{hasError && <small className="red">{hasError}</small>}
			</>
		);
	};

	const renderPartCell = (partKey, part, partIndex) => {
		const hasError = measurementErrors[partKey]?.[partIndex]?.part;
		if (!isEditable || !isRowEditing(partKey, partIndex)) {
			return (
				<>
					<p className={hasError ? 'text-danger' : ''}>{part.part || '-'}</p>
					{hasError && <small className="red">{hasError}</small>}
				</>
			);
		}
		return (
			<>
				<input
					className={`table-editable-input${hasError ? ' input-error' : ''}`}
					type="text"
					value={part.part || ''}
					autoFocus
					onChange={(e) => handleCellChange(partKey, partIndex, 'part', e.target.value)}
				/>
				{hasError && <small className="red">{hasError}</small>}
			</>
		);
	};

	const calculateTotalConsumption = (part) => {
		let length = (parseFloat(part.length_cm) || 0) + (parseFloat(part.allowance_length_cm) || 0);
		let width = (parseFloat(part.width_cm) || 0) + (parseFloat(part.allowance_width_cm) || 0);
		let pieces = parseInt(part.number_of_fabric_pieces) || 0;
		let gsm = parseInt(part.gsm) || 0;
		let wastage = parseFloat(part.wastage_percentage) || 0;
		return (length * width * pieces * gsm) / 10000000 / ((100 - wastage) / 100);
	};

	const saveMeasurement = (partKey) => {
		if (!validateMeasurements()) return;
		let tmpObj = {};
		Object.entries(localMeasurements).forEach(([partKey, measurements]) => {
			tmpObj[partKey] = measurements.map((measurement) => {
				return {
					...measurement,
					final_length_with_allowance_cm:
						(measurement.length_cm ? parseFloat(measurement?.length_cm) : 0) +
						(measurement.allowance_length_cm ? parseFloat(measurement?.allowance_length_cm) : 0),
					final_width_with_allowance_cm:
						(measurement.width_cm ? parseFloat(measurement?.width_cm) : 0) +
						(measurement.allowance_width_cm ? parseFloat(measurement?.allowance_width_cm) : 0)
				};
			});
		});
		onReCostMeasurement('MEASUREMENT_CHANGE', tmpObj);
		setChangedPartKeys((prevState) => prevState.delete(partKey) && new Set(prevState));
	};

	return (
		<LoaderComponent loading={loader}>
			<ModalContainer
				className={`width-1200-modal consumption-modal-container${loader ? ' dissabled-modal' : ''}`}
			>
				<div className="common-popup conumption-modal w-100 body-scroll">
					<div className="common-popup-header border-bottom-0 d-flex justify-content-between">
						<div className="popup-tilte w-75">
							<p className="semibold-14 gray_dark_01">Consumption Breakdown</p>
							<small className="regular-14 gray_dark_01">
								According to the measurement chart, fabric consumption has been calculated
								below:
							</small>
						</div>
						<div className="close-btn bg-white" onClick={onClose}>
							<CommonImage alt="close" src="/icons/close.svg" />
						</div>
					</div>
					<div className="common-popup-body scroll-y-label">
						{renderCadConsumption()}
						{Object.entries(localMeasurements).map(([partKey, parts], index) => {
							if (!Array.isArray(parts) || parts.length === 0) return null;

							const totalConsumption = parts.reduce((sum, part) => {
								const consumption = part.final_fabric_consumption_kg;
								const value =
									typeof consumption === 'string' ? parseFloat(consumption) : consumption;
								return sum + (isNaN(value) ? 0 : value);
							}, 0);

							const partTitle = getPartTitle(`part${index + 1}`);
							const isFirstTable = index === 0;

							return (
								<div
									key={partKey}
									className={`consumption-table scroll-x-label ${!isFirstTable ? 'mt-3' : ''}`}
								>
									<h3 className="regular-14 mb-2 d-flex align-items-center gap-1">
										Measurement Chart - {partTitle}
										{changedPartKeys.has(partKey) && (
											<span
												title="Unsaved changes"
												className="sync-icon ml-1 cursor-pointer"
												style={{
													display: 'inline-flex',
													alignItems: 'center',
													cursor: 'default'
												}}
												onClick={() => saveMeasurement(partKey)}
											>
												{renderSyncIcon()}
											</span>
										)}
									</h3>
									<table className="table height-sm with-border mb-0">
										<thead>
											<tr>
												<th>Body Part</th>
												<th>Length (cm)</th>
												<th>L. Allow (cm)</th>
												<th>Width (cm)</th>
												<th>W. Allow (cm)</th>
												<th>No. Fabric Pieces</th>
												<th>GSM</th>
												<th>Wastage (%)</th>
												<th>Consumption (Kg)</th>
											</tr>
										</thead>
										<tbody>
											{parts.map((part, partIndex) => {
												const isLastRow = partIndex === parts.length - 1;
												const rowIsEditing = isRowEditing(partKey, partIndex);

												return (
													<tr
														key={partIndex}
														ref={rowIsEditing ? editingRowRef : null}
														onClick={() => handleRowClick(partKey, partIndex)}
														style={{ cursor: isEditable ? 'pointer' : 'default' }}
														className={rowIsEditing ? 'row-editing' : ''}
													>
														<td className="body-part-column">
															{renderPartCell(partKey, part, partIndex)}
														</td>
														<td className="length-column">
															{renderCell(
																partKey,
																part,
																partIndex,
																'length_cm',
																part.length_cm
															)}
														</td>
														<td className="length-with-allowance-column">
															{renderCell(
																partKey,
																part,
																partIndex,
																'allowance_length_cm',
																part.allowance_length_cm
															)}
														</td>
														<td className="width-column">
															{renderCell(
																partKey,
																part,
																partIndex,
																'width_cm',
																part.width_cm
															)}
														</td>
														<td className="width-with-allowance-column">
															{renderCell(
																partKey,
																part,
																partIndex,
																'allowance_width_cm',
																part.allowance_width_cm
															)}
														</td>
														<td className="wastage-column">
															{renderCell(
																partKey,
																part,
																partIndex,
																'number_of_fabric_pieces',
																part.number_of_fabric_pieces
															)}
														</td>
														<td className="gsm-column">
															{renderCell(
																partKey,
																part,
																partIndex,
																'gsm',
																part.gsm
															)}
														</td>
														<td className="wastage-column">
															{renderCell(
																partKey,
																part,
																partIndex,
																'wastage_percentage',
																part.wastage_percentage
															)}
														</td>
														<td className={isLastRow ? 'consumption-column' : ''}>
															<p>
																{calculateTotalConsumption(part)?.toFixed(2)}
															</p>
														</td>
													</tr>
												);
											})}
										</tbody>
									</table>
									<div className="total-consumption table-footer p-2">
										<div className="d-flex justify-content-start">
											<p className="regular-14 gray_dark_01 weight-500">
												Total Consumption
											</p>
											<p className="regular-14 gray_dark_01 ml-2 weight-500">
												{totalConsumption.toFixed(2)} Kg
											</p>
										</div>
									</div>
								</div>
							);
						})}
					</div>
				</div>
			</ModalContainer>
		</LoaderComponent>
	);
};

export default ConsumptionModal;
